import React from 'react';

export interface TabItem {
  label: string | React.ReactNode;
  key: string;
  isActive: boolean;
}

interface TabSwitchListProperties {
  tabs: TabItem[];
  onSwitchTab: (key: string) => void;
  className?: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
}

/**
 * Renders a horizontal list of switchable tabs using the `each-tab` styling, wrapped in a
 * configurable container (defaults to the `device-tabs` styling used by device detail panels).
 * @param {TabSwitchListProperties} properties - The component props
 * @param {TabItem[]} properties.tabs - The tabs to render, each with a label, key, and active state
 * @param {(key: string) => void} properties.onSwitchTab - Called with the clicked tab's key
 * @param {string} [properties.className] - Wrapper class name, defaults to `device-tabs`
 * @param {React.CSSProperties} [properties.style] - Wrapper inline style, defaults to `{ flex: 1, borderBottom: 'none' }`
 * @param {boolean} [properties.fullWidth] - When true, stretches the wrapper to the parent's full width
 * @returns {React.ReactElement} The TabSwitchList component
 */
function TabSwitchList({ tabs, onSwitchTab, className, style, fullWidth }: TabSwitchListProperties) {
  const wrapperStyle = fullWidth ? { ...style, width: '100%' } : style;

  return (
    <div className={className} style={wrapperStyle}>
      {tabs.map((tab) => (
        <div
          key={tab.key}
          className={`each-tab ${tab.isActive ? 'active' : ''}`}
          role="presentation"
          onClick={() => onSwitchTab(tab.key)}>
          <h6>{tab.label}</h6>
          <div className="bottom-green" />
        </div>
      ))}
    </div>
  );
}

TabSwitchList.defaultProps = {
  className: 'device-tabs',
  style: { flex: 1, borderBottom: 'none' },
  fullWidth: false,
};

export default TabSwitchList;
